import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { dispatcher } from 'dispatcher';
import { pick } from 'ramda';

import { useTranslations } from '@core/js/hooks/i18n';
import { setPlayerTags } from '@core/js/hooks/user/server';
import { useShowNotification } from '@core/js/hooks/useShowNotification';
import { storageService } from '@core/js/services/storage.service';
import { useUserSelector } from '@core/js/store/hooks';

import { ENTER_DRAW_ACTION } from '@interlayer/js/app/components/main/Promotions/constants';
import { showContactInfoUpdatePopup } from '@interlayer/js/app/components/popups/ContactInfoUpdatePopup/actions';
import { showEnterDrawPopup } from '@interlayer/js/app/components/popups/EnterDrawPopup/actions';
import { useIsVipLevel } from '@interlayer/js/app/hooks/auth/hooks/useIsVipLevel';
import { useAllPlayerTags } from '@interlayer/js/app/hooks/user/hooks/useAllPlayerTags';
import { useLoginPopup } from '@interlayer/js/components/popups/LoginPopup/hooks';

import { TUseEnterDrawProps } from '../types';

export const useEnterDraw = (props: TUseEnterDrawProps) => {
    const { pathname } = useLocation();
    const t = useTranslations('playtech.mobile.system.enter-draw.');
    const showNotification = useShowNotification({ variant: 'info' });

    const {
        index,
        title,
        type,
        mainTag = '',
        secondaryTags = [],
        contactPopupKQCText = t('contact-popup.terms'),
        successPopupTitleText = t('success.title'),
        successPopupText = t('success.text'),
        successPopupCTAText = t('success.cta'),
        errorPopupTitleText = t('error.title'),
        errorPopupText = t('error.text'),
        errorPopupCTAText = t('error.cta'),
        showLoginForGuest,
        showContactPopup,
        successCTACallback,
        ctaConfiguration,
        navigateUrl,
        gameCode,
        showInfos = false
    } = props;

    const dispatch = useDispatch();
    const showLoginPopup = useLoginPopup();
    const isVipLevel = useIsVipLevel();

    // @ts-ignore TODO: Remove ts ignore when useAllPlayerTags will be rewrote on typeScript
    const { state: { isLoading }, fetch: fetchAllPlayerTags } = useAllPlayerTags({ isForceFetch: true });
    const { isLoggedIn, allPlayerTags: playerTags, userDetails: { playerCode } } = useUserSelector(pick(['isLoggedIn', 'allPlayerTags', 'userDetails']));

    const isSet = playerTags?.[mainTag];
    const isDisabled = secondaryTags.find(({ secondaryTag }) => playerTags?.[secondaryTag]);
    const isActionSuppressed = isSet || isDisabled || isLoading;

    const updatePlayerTags = useCallback(() => {
        const enterDrawParams = {
            gameCode,
            navigateUrl,
            errorPopupText,
            successPopupText,
            ctaConfiguration,
            errorPopupCTAText,
            successCTACallback,
            errorPopupTitleText,
            successPopupCTAText,
            successPopupTitleText
        };

        const showPopup = (isSuccessUpdateTag: boolean) => {
            dispatch(showEnterDrawPopup({ enterDrawParams: { ...enterDrawParams, isSuccessUpdateTag } }));
        };

        return setPlayerTags([{ name: mainTag }])
            .then(() => {
                if (index && type) {
                    dispatcher.dispatch(`${type}Position`, {
                        page: pathname,
                        action: ENTER_DRAW_ACTION,
                        position: index + 1,
                        link: mainTag,
                        title
                    });
                }

                return fetchAllPlayerTags({ isUpdate: true });
            })
            .then(() => {
                showPopup(true);
            })
            .catch(() => {
                showPopup(false);
            });

    }, [
        index,
        type,
        title,
        pathname,
        mainTag,
        dispatch,
        gameCode,
        navigateUrl,
        errorPopupText,
        successPopupText,
        ctaConfiguration,
        errorPopupCTAText,
        fetchAllPlayerTags,
        successCTACallback,
        errorPopupTitleText,
        successPopupCTAText,
        successPopupTitleText
    ]);

    return useCallback(() => {
        if (isActionSuppressed) {
            if (showInfos) {
                isSet && showNotification(t('info.set'));
                isDisabled && showNotification(t('info.disabled'));
            }

            return;
        }

        const handlerForLoggedInUser = () => {
            if (showContactPopup && (isVipLevel || !storageService.local.getItem(`contactInfoChecked_${playerCode}`))) {
                dispatch(showContactInfoUpdatePopup({
                    terms: contactPopupKQCText,
                    onClose: updatePlayerTags,
                    isSetToLocalStorage: true
                }));
            } else {
                updatePlayerTags();
            }
        };

        if (isLoggedIn) {
            handlerForLoggedInUser();
        } else if (showLoginForGuest) {
            showLoginPopup({
                onLogin: () => {
                    handlerForLoggedInUser();
                }
            });
        }
    }, [
        t,
        isSet,
        dispatch,
        showInfos,
        isVipLevel,
        playerCode,
        isDisabled,
        isLoggedIn,
        showLoginPopup,
        updatePlayerTags,
        showContactPopup,
        showLoginForGuest,
        isActionSuppressed,
        contactPopupKQCText,
        showNotification
    ]);
};
